import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Upload, Trash2, Search, X } from 'lucide-react';
import { getToken } from '../../services/tokenService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = getToken();
  return {
    'Authorization': `Bearer ${token}`
  };
};

const MediaLibrary = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedMedia, setSelectedMedia] = useState(null);

  const categories = ['all', 'logos', 'banners', 'icons', 'illustrations', 'general'];

  useEffect(() => {
    loadMedia();
  }, [selectedCategory]);

  const loadMedia = async () => {
    setLoading(true);
    try {
      const categoryParam = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
      const response = await fetch(`${API_URL}/api/cms/media${categoryParam}`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setMedia(data.media || []);
    } catch {
    }
    setLoading(false);
  };

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingFile(file.name);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('alt_text', file.name.split('.')[0]);
    formData.append('category', selectedCategory === 'all' ? 'general' : selectedCategory);

    try {
      const response = await fetch(`${API_URL}/api/cms/media/upload`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      if (response.ok) {
        await loadMedia();
        setUploadingFile(null);
      } else {
        alert('Erreur lors de l\'upload');
      }
    } catch {
      alert('Erreur lors de l\'upload');
    }
    setUploadingFile(null);
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('Supprimer ce média ?')) return;

    try {
      const response = await fetch(`${API_URL}/api/cms/media/${mediaId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        await loadMedia();
      }
    } catch {
    }
  };

  const filteredMedia = media.filter(m =>
    m.original_filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.alt_text?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const copyUrl = (url) => {
    navigator.clipboard.writeText(`${API_URL}${url}`);
    alert('URL copiée !');
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Médiathèque</CardTitle>
            <p className="text-sm text-gray-500 mt-1">{media.length} fichier(s)</p>
          </div>
          <div className="flex gap-2">
            <label htmlFor="file-upload">
              <Button as="span" className="cursor-pointer">
                <Upload className="h-4 w-4 mr-2" />
                Uploader
              </Button>
              <input
                id="file-upload"
                type="file"
                accept="image/*"
                onChange={handleUpload}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Filters */}
        <div className="mb-6 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Rechercher un média..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex gap-2 flex-wrap">
            {categories.map(cat => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat)}
              >
                {cat === 'all' ? 'Tous' : cat.charAt(0).toUpperCase() + cat.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {/* Upload Progress */}
        {uploadingFile && (
          <div className="mb-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-900">Upload en cours : {uploadingFile}...</p>
          </div>
        )}

        {/* Media Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Chargement...</p>
          </div>
        ) : filteredMedia.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
            <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500 mb-2">Aucun média trouvé</p>
            <p className="text-sm text-gray-400">Uploadez votre premier fichier</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredMedia.map(m => (
              <div
                key={m.id}
                className="group relative border rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => setSelectedMedia(m)}
              >
                <div className="aspect-square bg-gray-100 flex items-center justify-center">
                  <img
                    src={`${API_URL}${m.url}`}
                    alt={m.alt_text}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-2">
                  <p className="text-xs font-medium truncate">{m.original_filename}</p>
                  <p className="text-xs text-gray-500">
                    {m.width}x{m.height} • {(m.file_size / 1024).toFixed(1)}KB
                  </p>
                </div>
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity space-x-1">
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(m.id);
                    }}
                    className="h-8 w-8 p-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Media Detail Modal */}
        {selectedMedia && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSelectedMedia(null)}>
            <Card className="max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle>Détails du média</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedMedia(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <img
                    src={`${API_URL}${selectedMedia.url}`}
                    alt={selectedMedia.alt_text}
                    className="w-full rounded-lg"
                  />
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Nom : </span>
                      {selectedMedia.original_filename}
                    </div>
                    <div>
                      <span className="font-medium">Dimensions : </span>
                      {selectedMedia.width}x{selectedMedia.height}px
                    </div>
                    <div>
                      <span className="font-medium">Taille : </span>
                      {(selectedMedia.file_size / 1024).toFixed(1)}KB
                    </div>
                    <div>
                      <span className="font-medium">Catégorie : </span>
                      {selectedMedia.category}
                    </div>
                    <div>
                      <span className="font-medium">URL : </span>
                      <code className="bg-gray-100 px-2 py-1 rounded text-xs">{API_URL}{selectedMedia.url}</code>
                      <Button size="sm" variant="ghost" onClick={() => copyUrl(selectedMedia.url)} className="ml-2">
                        Copier
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MediaLibrary;